import {
  Button,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  Icons,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Stack,
  styled,
  Typography,
} from '@healthinal/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useCreateAnamnesisSymptom } from '../../../../../api/generated/anamnesis-controller/anamnesis-controller.ts';
import { SymptomDtoSymptomType } from '../../../../../api/generated/model';
import { getGetAnamnesesByPatientQueryKey } from '../../../../../api/generated/patient-controller/patient-controller.ts';
import { FormActionButtons } from '../../../../../components/form/FormActionButtons.tsx';
import { GermanDateInput } from '../../../../../components/form/GermanDateInput.tsx';
import { getSymptomTypeName } from '../../../../../utils/anamnesis.ts';

interface FormValues {
  symptoms: {
    started: string;
    symptomType?: SymptomDtoSymptomType;
  }[];
}

const emptySymptomRow = {
  started: '',
  symptomType: undefined,
};

export function AddSymptomDialog({
  anamnesisId,
  patientId,
  open,
  onClose,
}: {
  anamnesisId: string;
  patientId: string;
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();

  const {
    control,
    reset,
    handleSubmit,
    watch,
    formState: { errors, isDirty: isFormDirty },
  } = useForm<FormValues>({
    defaultValues: {
      symptoms: [emptySymptomRow],
    },
  });

  const symptomRows = watch('symptoms');
  const hasIncompleteRows = symptomRows.some((symptomRow) => !symptomRow.symptomType);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'symptoms',
  });

  const createSymptomMutation = useCreateAnamnesisSymptom({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetAnamnesesByPatientQueryKey(patientId) });
        handleOnClose();
      },
      onError: () => {
        toast.error('Beim Hinzufügen des Symptoms ist ein Fehler aufgetreten');
      },
    },
  });

  const handleOnClose = () => {
    reset();
    onClose();
  };

  const handleFormSubmit = (data: FormValues) => {
    createSymptomMutation.mutate({
      id: anamnesisId,
      data: data.symptoms.map((symptomData) => ({
        started: symptomData.started || undefined,
        symptomType: symptomData.symptomType as SymptomDtoSymptomType,
      })),
    });
  };

  return (
    <Modal open={open} onClose={handleOnClose}>
      <ModalDialog sx={{ width: 700, maxHeight: '90vh', overflowY: 'auto' }}>
        <ModalClose />
        <Typography level={'h4'}>Symptom hinzufügen</Typography>
        <Divider />
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <Stack gap={2}>
            {fields.map((field, index) => (
              <FormGrid key={field.id}>
                <FormControl error={!!errors.symptoms?.[index]?.started}>
                  <FormLabel>
                    <Typography>Seit</Typography>
                  </FormLabel>
                  <GermanDateInput
                    dateInputMode="freeText"
                    control={control}
                    name={`symptoms.${index}.started` as const}
                  />
                </FormControl>
                <FormControl error={!!errors.symptoms?.[index]?.symptomType}>
                  <FormLabel>
                    <Typography>Symptom *</Typography>
                  </FormLabel>
                  <Controller
                    name={`symptoms.${index}.symptomType`}
                    control={control}
                    rules={{ required: true }}
                    render={({ field: { onChange, value } }) => (
                      <Select value={value} onChange={(_, value) => onChange(value)}>
                        {Object.values(SymptomDtoSymptomType).map((type) => (
                          <Option value={type} key={type}>
                            {getSymptomTypeName(type)}
                          </Option>
                        ))}
                      </Select>
                    )}
                  />
                  {errors.symptoms?.[index]?.symptomType && (
                    <FormHelperText>Symptom muss angegeben werden</FormHelperText>
                  )}
                </FormControl>
                <IconButton
                  color={'danger'}
                  disabled={fields.length === 1}
                  onClick={() => remove(index)}
                  title={'Eintrag entfernen'}
                  type={'button'}
                  sx={{ alignSelf: 'end' }}>
                  <Icons.Delete />
                </IconButton>
              </FormGrid>
            ))}
            <Button
              color={'primary'}
              disabled={hasIncompleteRows}
              endDecorator={<Icons.Add />}
              onClick={() => append(emptySymptomRow)}
              title={'Weitere Zeile hinzufügen'}
              type={'button'}
              sx={{ alignSelf: 'flex-end' }}>
              Zeile hinzufügen
            </Button>
            <FormActionButtons
              onAbortClick={handleOnClose}
              disableSubmit={!isFormDirty || hasIncompleteRows}
              isLoading={createSymptomMutation.isPending}
            />
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
}

const FormGrid = styled('div')`
  display: grid;
  gap: 0.75rem 1rem;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto;
  align-items: start;
`;
